import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import { useApexOnlyPage } from '../../hooks/useApexOnlyPage';
import { db } from '../../firebase';
import { buildApexCanonicalUrl } from '../../lib/siteUrls';

const initialFormState = {
  name: '',
  email: '',
  itineraryId: '',
  url: '',
  message: '',
};

const ContactSupport = () => {
  const isRedirecting = useApexOnlyPage('/support');
  const [form, setForm] = useState(initialFormState);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  if (isRedirecting) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === 'submitting') return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Name, email, and your message are required.');
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      await addDoc(collection(db, 'supportRequests'), {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        itineraryId: form.itineraryId.trim() || null,
        url: form.url.trim() || null,
        message: form.message.trim(),
        submittedAt: serverTimestamp(),
      });

      setStatus('success');
      setForm(initialFormState);
    } catch (err) {
      console.error('Failed to submit support request', err);
      setStatus('error');
      setError('Something went wrong while sending your message. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Support | Alakaʻi Aloha</title>
        <meta
          name="description"
          content="Contact Alakaʻi Aloha for itinerary support, billing questions, or help with island-specific travel content."
        />
        <link rel="canonical" href={buildApexCanonicalUrl('/support')} />
      </Helmet>

      <div className="max-w-4xl mx-auto py-4 px-6 sm:px-8">
        <header className="mb-6 space-y-2">
          <h1 className="text-3xl font-semibold">Contact &amp; Support</h1>
          <p className="text-base leading-relaxed text-white/80">
            Questions about your itinerary, billing, an island guide, or anything else? Send us a note and we&apos;ll
            reply as soon as possible.
          </p>
        </header>

      {status === 'success' ? (
        <div className="rounded-lg bg-white/10 border border-white/10 p-6 text-center">
          <h2 className="text-lg font-semibold mb-2">Mahalo for reaching out!</h2>
          <p className="text-sm text-white/80">
            We have your message and will follow up shortly. Feel free to close this page or submit another request if
            there&apos;s more to share.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="rounded-lg border border-white/10 bg-transparent p-6 space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-white mb-1">
              Name <span className="text-rose-300">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className="w-full rounded-md bg-white/10 border border-white/10 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Your name"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-white mb-1">
              Email <span className="text-rose-300">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className="w-full rounded-md bg-white/10 border border-white/10 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="itineraryId" className="block text-sm font-semibold text-white mb-1">
              Interview / Itinerary ID (optional)
            </label>
            <input
              id="itineraryId"
              name="itineraryId"
              type="text"
              value={form.itineraryId}
              onChange={handleChange}
              className="w-full rounded-md bg-white/10 border border-white/10 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="e.g. C6GPrHjBn2L0P90kbkdA"
            />
            <p className="mt-1 text-xs text-white/60">
              Add this if your question is about a specific plan so we can pull it up quickly.
            </p>
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-semibold text-white mb-1">
              URL (optional)
            </label>
            <input
              id="url"
              name="url"
              type="url"
              value={form.url}
              onChange={handleChange}
              className="w-full rounded-md bg-white/10 border border-white/10 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="https://your-island.alakaialoha.com/..."
            />
            <p className="mt-1 text-xs text-white/60">
              Add this if your question references a specific page on one of our sites.
            </p>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-white mb-1">
              How can we help? <span className="text-rose-300">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              className="w-full rounded-md bg-white/10 border border-white/10 px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Share a bit about what you need or any context that will help us respond."
              required
            />
          </div>

          {error && <p className="text-sm text-rose-300">{error}</p>}

          <button
            type="submit"
            className={`btn-primary-cta w-full ${status === 'submitting' ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Sending…' : 'Send message'}
          </button>

          {status === 'error' && !error && (
            <p className="text-sm text-rose-300">Something went wrong while sending your message. Please try again.</p>
          )}
        </form>
      )}
      </div>
    </>
  );
};

export default ContactSupport;
